import { useCallback, useMemo } from 'react';

import { useStatefulRef } from '../../utils/hooks/useStatefulRef';

export interface Item<T> {
  id: string;
  timestamp: number;
  data: T;
}

interface UseItemRegistryResult<T> {
  add: (...items: Array<Item<T>>) => void;
  clear: () => void;
  filter: (predicate: (item: Item<T>) => boolean) => void;
  get: (id: string) => Item<T> | undefined;
  getAll: () => Array<Item<T>>;
  hasItem: (id: string) => boolean;
  remove: (id: string) => void;
  size: () => number;
}

/**
 * A hook that provides a registry for managing timestamped items with unique IDs.
 *
 * The registry maintains a collection of items where:
 * - When adding items with the same ID, only the item with the most recent timestamp is kept
 * - getter methods hold a ref to the registry so they are updated when the registry is updated,
 *   and can be used to get the latest values in async operations
 */
export const useItemRegistry = <T,>(): UseItemRegistryResult<T> => {
  const [getQueue, setQueue] = useStatefulRef<Map<string, Item<T>>>(new Map());

  const setItemIfNewer = useCallback(
    (queue: Map<string, Item<T>>, item: Item<T>) => {
      const existingItem = queue.get(item.id);
      if (!existingItem || item.timestamp > existingItem.timestamp) {
        queue.set(item.id, item);
      }
    },
    []
  );

  const add = useCallback(
    (...items: Array<Item<T>>) =>
      setQueue((queue) => {
        const newQueue = new Map(queue);
        items.forEach((item) => setItemIfNewer(newQueue, item));
        return newQueue;
      }),
    [setItemIfNewer, setQueue]
  );

  const filter = useCallback(
    (predicate: (item: Item<T>) => boolean) => {
      setQueue(
        (queue) => new Map([...queue].filter(([, item]) => predicate(item)))
      );
    },
    [setQueue]
  );

  const remove = useCallback(
    (id: string) => {
      setQueue((queue) => {
        const newQueue = new Map(queue);
        newQueue.delete(id);
        return newQueue;
      });
    },
    [setQueue]
  );

  const clear = useCallback(() => setQueue(new Map()), [setQueue]);

  const get = useCallback((id: string) => getQueue().get(id), [getQueue]);

  const getAll = useCallback(() => Array.from(getQueue().values()), [getQueue]);

  const hasItem = useCallback((id: string) => getQueue().has(id), [getQueue]);

  const size = useCallback(() => getQueue().size, [getQueue]);

  return useMemo(
    () => ({
      add,
      clear,
      filter,
      get,
      getAll,
      hasItem,
      remove,
      size,
    }),
    [add, clear, filter, get, getAll, hasItem, remove, size]
  );
};
